import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordGameScore, getCurrentUser, calculateScore, determineClassLevel, fetchGameQuestions, getGameTypeFromId } from '../utils/gameUtils';

const MultiplicationMonsters = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [question, setQuestion] = useState(null);
  const [monsters, setMonsters] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [playerPos, setPlayerPos] = useState(50);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameOver, setGameOver] = useState(false);
  const [combo, setCombo] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [scoreRecorded, setScoreRecorded] = useState(false);
  const [questionPool, setQuestionPool] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const gameAreaRef = useRef(null);

  // Load questions from API on component mount
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const gameType = getGameTypeFromId('multiplication-monsters');
        const questions = await fetchGameQuestions(4, gameType, 50); // Load 50 questions for the game
        setQuestionPool(questions);
        console.log('Loaded questions for Multiplication Monsters:', questions.length);
      } catch (error) {
        console.error('Failed to load questions:', error);
      }
    };
    
    loadQuestions();
  }, []);

  const generateQuestion = () => {
    if (questionPool.length === 0) {
      // Fallback to old method if no questions loaded
      const num1 = Math.floor(Math.random() * (10 + level * 5)) + 10;
      const num2 = Math.floor(Math.random() * (10 + level * 5)) + 10;
      return { num1, num2, answer: num1 * num2 };
    }
    
    // Use questions from the pool
    const questionData = questionPool[currentQuestionIndex % questionPool.length];
    setCurrentQuestionIndex(prev => prev + 1);
    
    // Extract numbers from question text (e.g., "What is 24 × 15?")
    const questionText = questionData.question;
    const matches = questionText.match(/(\d+)\s*×\s*(\d+)/);
    
    if (matches) {
      return {
        num1: parseInt(matches[1]),
        num2: parseInt(matches[2]),
        answer: parseInt(questionData.choices[questionData.answer]),
        questionData: questionData
      };
    } else {
      // Fallback if question format doesn't match
      const num1 = Math.floor(Math.random() * (10 + level * 5)) + 10;
      const num2 = Math.floor(Math.random() * (10 + level * 5)) + 10;
      return { num1, num2, answer: num1 * num2 };
    }
  };

  const spawnMonster = () => {
    const newQuestion = generateQuestion();
    
    let allAnswers;
    if (newQuestion.questionData) {
      // Use choices from API question
      allAnswers = [...newQuestion.questionData.choices];
    } else {
      // Generate wrong answers for fallback questions
      const wrongAnswers = [
        newQuestion.answer + Math.floor(Math.random() * 50) + 10,
        newQuestion.answer - Math.floor(Math.random() * 50) - 10,
        newQuestion.answer + Math.floor(Math.random() * 100) + 20
      ];
      allAnswers = [newQuestion.answer, ...wrongAnswers];
    }
    
    // Shuffle answers and take first 3
    const shuffledAnswers = allAnswers.sort(() => Math.random() - 0.5).slice(0, 3);

    const newMonsters = shuffledAnswers.map((ans, idx) => ({
      id: Date.now() + idx,
      answer: ans,
      isCorrect: ans == newQuestion.answer, // Use == for type coercion
      x: 20 + (idx * 30),
      y: -10,
      speed: 0.5 + level * 0.1
    }));

    setQuestion(newQuestion);
    setMonsters(newMonsters);
  };

  useEffect(() => {
    spawnMonster();
  }, [level]);

  // Record score when game ends
  useEffect(() => {
    if (gameOver && !scoreRecorded && totalQuestions > 0) {
      const recordScore = async () => {
        try {
          const user = getCurrentUser();
          if (user) {
            const finalScore = calculateScore(correctAnswers, totalQuestions);
            const classLevel = determineClassLevel('MultiplicationMonsters', level);
            
            await recordGameScore({
              userId: user.id,
              class: classLevel,
              score: finalScore,
              correct: correctAnswers,
              total: totalQuestions
            });
            
            setScoreRecorded(true);
            console.log(`🎉 Score recorded: ${correctAnswers}/${totalQuestions} (${finalScore}%)`);
          }
        } catch (error) {
          console.error('Failed to record score:', error);
        }
      };
      
      recordScore();
    }
  }, [gameOver, scoreRecorded, totalQuestions, correctAnswers, level]);

  useEffect(() => {
    if (timeLeft > 0 && !gameOver) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setGameOver(true);
    }
  }, [timeLeft, gameOver]);

  useEffect(() => {
    const gameLoop = setInterval(() => {
      if (gameOver) return;

      // Move monsters down
      setMonsters(prev => {
        const updated = prev.map(m => ({ ...m, y: m.y + m.speed }));
        // Check if any monster reached bottom
        if (updated.some(m => m.y > 100)) {
          spawnMonster();
          return [];
        }
        return updated;
      });

      // Move bullets up
      setBullets(prev => prev
        .map(b => ({ ...b, y: b.y - 2 }))
        .filter(b => b.y > -5)
      );
    }, 50);

    return () => clearInterval(gameLoop);
  }, [gameOver]);

  useEffect(() => {
    // Check collisions
    bullets.forEach(bullet => {
      monsters.forEach(monster => {
        const distance = Math.sqrt(
          Math.pow(bullet.x - monster.x, 2) + 
          Math.pow(bullet.y - monster.y, 2)
        );
        
        if (distance < 5) {
          // Track total questions answered
          setTotalQuestions(prev => prev + 1);
          
          if (monster.isCorrect) {
            setScore(s => s + (10 * (combo + 1)));
            setCombo(c => c + 1);
            setCorrectAnswers(prev => prev + 1);
            if (combo > 0 && combo % 3 === 0) {
              setLevel(l => l + 1);
            }
            spawnMonster();
          } else {
            setCombo(0);
          }
          setBullets(b => b.filter(bu => bu.id !== bullet.id));
          setMonsters(m => m.filter(mo => mo.id !== monster.id));
        }
      });
    });
  }, [bullets, monsters, combo]);

  const handleKeyPress = (e) => {
    if (gameOver) return;
    
    if (e.key === 'ArrowLeft') {
      setPlayerPos(p => Math.max(10, p - 5));
    } else if (e.key === 'ArrowRight') {
      setPlayerPos(p => Math.min(90, p + 5));
    } else if (e.key === ' ') {
      e.preventDefault();
      shoot();
    }
  };

  const shoot = () => {
    setBullets(prev => [...prev, {
      id: Date.now(),
      x: playerPos,
      y: 90
    }]);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [playerPos, gameOver]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-700 to-pink-600 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-4 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="text-white hover:text-yellow-300">
              ← Back
            </button>
            <div className="text-center">
              <h1 className="text-2xl font-bold">👾 Multiplication Monsters</h1>
              {question && (
                <div className="text-3xl font-bold mt-2">
                  {question.num1} × {question.num2} = ?
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm">Level {level}</div>
              <div className="text-2xl font-bold">{score}</div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">⏱️</div>
            <div className="text-xl font-bold">{timeLeft}s</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">🔥</div>
            <div className="text-xl font-bold">x{combo + 1}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">🎯</div>
            <div className="text-xl font-bold">{level}</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 text-white text-center">
            <div className="text-2xl">⭐</div>
            <div className="text-xl font-bold">{score}</div>
          </div>
        </div>

        {!gameOver ? (
          /* Game Area */
          <div 
            ref={gameAreaRef}
            className="relative bg-black/30 rounded-xl overflow-hidden"
            style={{ height: '500px' }}
            tabIndex={0}
          >
            {/* Monsters */}
            {monsters.map(monster => (
              <div
                key={monster.id}
                className={`absolute transition-all duration-100 ${
                  monster.isCorrect ? 'bg-green-500' : 'bg-red-500'
                } rounded-lg p-4 text-white font-bold text-xl shadow-lg`}
                style={{
                  left: `${monster.x}%`,
                  top: `${monster.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">👾</div>
                  <div className="text-sm">{monster.answer}</div>
                </div>
              </div>
            ))}

            {/* Bullets */}
            {bullets.map(bullet => (
              <div
                key={bullet.id}
                className="absolute bg-yellow-400 rounded-full"
                style={{
                  left: `${bullet.x}%`,
                  top: `${bullet.y}%`,
                  width: '8px',
                  height: '16px',
                  transform: 'translate(-50%, -50%)'
                }}
              />
            ))}

            {/* Player */}
            <div
              className="absolute bottom-4 transition-all duration-100"
              style={{
                left: `${playerPos}%`,
                transform: 'translateX(-50%)'
              }}
            >
              <div className="text-5xl">🚀</div>
            </div>

            {/* Controls Hint */}
            <div className="absolute bottom-4 left-4 text-white text-sm bg-black/50 p-2 rounded">
              ← → Move | SPACE Shoot
            </div>
          </div>
        ) : (
          /* Game Over */
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Game Over!</h2>
            <div className="text-xl text-gray-600 mb-6">
              <p>Questions Answered: <span className="font-bold text-purple-600">{totalQuestions}</span></p>
              <p>Correct Answers: <span className="font-bold text-purple-600">{correctAnswers}</span></p>
              <p>Accuracy: <span className="font-bold text-purple-600">{totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0}%</span></p>
              <p>Level Reached: <span className="font-bold text-purple-600">{level}</span></p>
              <p>Best Combo: <span className="font-bold text-purple-600">x{combo + 1}</span></p>
              {scoreRecorded && (
                <p className="text-green-600 font-bold mt-2">✅ Score Recorded!</p>
              )}
            </div>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-purple-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-purple-700"
              >
                Play Again
              </button>
              <button
                onClick={() => navigate('/games')}
                className="bg-gray-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-700"
              >
                Back to Games
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiplicationMonsters;